<template>
  <ToastContainer />
  <div class="app-shell">
    <header class="app-header">
      <div class="brand">
        <button
          v-if="auth.isAuthenticated"
          type="button"
          class="menu-toggle"
          aria-label="Navigation umschalten"
          @click="toggleMenu"
        >
          <span />
          <span />
          <span />
        </button>
        <span class="dot" />
        <h1>Roter Faden</h1>
      </div>
      <nav
        v-if="auth.isAuthenticated"
        :class="['nav-links', { open: isMenuOpen }]"
      >
        <RouterLink to="/">Dashboard</RouterLink>

        <!-- Neu erstellen Dropdown -->
        <div class="dropdown" @mouseenter="showCreateMenu = true" @mouseleave="hideCreateMenuDelayed">
          <button class="nav-dropdown-trigger" type="button">
            Neu erstellen ▾
          </button>
          <div v-if="showCreateMenu" class="dropdown-menu" @mouseenter="cancelHideCreateMenu" @mouseleave="hideCreateMenuDelayed">
            <RouterLink to="/protocols/new" class="dropdown-item">
              📋 Protokoll erstellen
            </RouterLink>
            <RouterLink to="/resolutions" class="dropdown-item">
              📜 Umlaufbeschluss erstellen
            </RouterLink>
            <RouterLink to="/agendas" class="dropdown-item">
              📝 Tagesordnung erstellen
            </RouterLink>
            <RouterLink to="/todos" class="dropdown-item">
              ✅ Aufgabe erstellen
            </RouterLink>
          </div>
        </div>

        <RouterLink to="/search">Suche</RouterLink>
        <RouterLink to="/documents">Dokumente</RouterLink>
        <RouterLink to="/faq">FAQ</RouterLink>
        <RouterLink v-if="auth.isSysadmin" to="/admin">Admin</RouterLink>
      </nav>
      <div class="auth-area">
        <RouterLink v-if="auth.isAuthenticated" to="/settings/notifications" class="icon-link" title="Benachrichtigungen">
          🔔
        </RouterLink>
        <RouterLink v-if="auth.isAuthenticated" to="/profile" class="user-pill">
          {{ auth.displayName || auth.userEmail }}
        </RouterLink>
        <button v-if="auth.isAuthenticated" class="logout" @click="logout">Abmelden</button>
        <RouterLink v-else to="/login">Anmelden</RouterLink>
      </div>
    </header>
    <main>
      <RouterView />
    </main>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { RouterLink, RouterView, useRouter } from 'vue-router';
import { useAuthStore } from './store/auth';
import ToastContainer from './components/ToastContainer.vue';

const auth = useAuthStore();
const router = useRouter();
const isMenuOpen = ref(false);
const showCreateMenu = ref(false);
let hideMenuTimeout = null;

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const hideCreateMenuDelayed = () => {
  hideMenuTimeout = setTimeout(() => {
    showCreateMenu.value = false;
  }, 200);
};

const cancelHideCreateMenu = () => {
  if (hideMenuTimeout) {
    clearTimeout(hideMenuTimeout);
    hideMenuTimeout = null;
  }
  showCreateMenu.value = true;
};

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    isMenuOpen.value = false;
    showCreateMenu.value = false;
    if (hideMenuTimeout) {
      clearTimeout(hideMenuTimeout);
      hideMenuTimeout = null;
    }
  }
);

const logout = () => {
  auth.logout();
  isMenuOpen.value = false;
  router.push({ name: 'login' });
};
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #ffffff;
  border-bottom: 1px solid var(--border);
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.brand h1 {
  font-size: 1.25rem;
  margin: 0;
  font-weight: 700;
}

.dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--brand);
}

.nav-links {
  display: flex;
  gap: 1rem;
  flex: 1;
}

.nav-links a {
  color: var(--muted);
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
}

.nav-links a:hover {
  color: var(--brand);
}

.nav-links a.router-link-active {
  color: var(--brand);
}

/* Dropdown styles */
.dropdown {
  position: relative;
  display: inline-block;
}

.nav-dropdown-trigger {
  background: transparent;
  border: none;
  color: var(--muted);
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: color 0.2s;
}

.nav-dropdown-trigger:hover {
  color: var(--brand);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.5rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  z-index: 100;
  overflow: hidden;
}

.dropdown-item {
  display: block;
  padding: 0.75rem 1rem;
  color: #333;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  transition: background 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: rgba(220, 38, 38, 0.05);
  color: var(--brand);
}

.auth-area {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.icon-link {
  font-size: 1.3rem;
  line-height: 1;
  text-decoration: none;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.icon-link:hover {
  opacity: 1;
}

.user-pill {
  padding: 0.35rem 0.75rem;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  font-size: 0.85rem;
}

.logout {
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.35rem 0.9rem;
}

.menu-toggle {
  display: none;
  width: 42px;
  height: 42px;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0;
}

.menu-toggle span {
  width: 20px;
  height: 2px;
  background: var(--text);
  border-radius: 999px;
  display: block;
}

main {
  flex: 1;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem 1.5rem 3rem;
  box-sizing: border-box;
}

@media (max-width: 960px) {
  main {
    padding: 1.75rem 1.25rem 2.5rem;
  }
}

@media (max-width: 720px) {
  .app-header {
    flex-wrap: wrap;
    padding: 0.75rem 1rem;
    gap: 0.5rem;
  }

  .brand {
    flex: 1;
    justify-content: flex-start;
  }

  .brand h1 {
    font-size: 1.1rem;
  }

  .menu-toggle {
    display: inline-flex;
  }

  .nav-links {
    order: 3;
    width: 100%;
    display: none;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem 0.5rem 0.25rem;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }

  .nav-links.open {
    display: flex;
  }

  /* Mobile dropdown adjustments */
  .dropdown-menu {
    position: static;
    margin-top: 0.5rem;
    box-shadow: none;
    border: none;
    border-left: 2px solid var(--brand);
    padding-left: 0.5rem;
  }

  .dropdown-item {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }

  .auth-area {
    order: 2;
    width: auto;
  }

  main {
    padding: 1.5rem 0.9rem 2rem;
  }
}

@media (max-width: 480px) {
  .auth-area {
    gap: 0.5rem;
  }

  .logout {
    padding: 0.3rem 0.75rem;
  }
}
</style>
